import React from "react";
import { Settings } from "lucide-react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { FormData, FormErrors, sanitizeEmail } from "../../../hooks/useCreateRecordForm";

interface Step4ConvenioRepresentanteProps {
  formData: FormData;
  errors: FormErrors;
  updateFormData: <K extends keyof FormData>(key: K, value: FormData[K]) => void;
  validateField: (fieldName: keyof FormData) => boolean;
  isAnimating: boolean;
}

export const Step4ConvenioRepresentante: React.FC<Step4ConvenioRepresentanteProps> = ({
  formData,
  errors,
  updateFormData,
  validateField,
  isAnimating,
}) => {
  const convenioSelection: "yes" | "no" | undefined =
    formData.alternativeJustice === null
      ? undefined
      : formData.alternativeJustice
        ? "yes"
        : "no";

  const representanteSelection: "yes" | "no" | undefined =
    formData.requiresOwnerRepresentative === null
      ? undefined
      : formData.requiresOwnerRepresentative
        ? "yes"
        : "no";

  const convenioValue = convenioSelection ?? "";
  const representanteValue = representanteSelection ?? "";

  const showRepresentanteStep = formData.alternativeJustice === true;

  const handleConvenioChange = (value: string) => {
    if (value !== "yes" && value !== "no") return;

    updateFormData("alternativeJustice", value === "yes");
    if (value === "no") {
      updateFormData("landlordType", "fisica");
      updateFormData("landlordEmail", "");
      updateFormData("requiresOwnerRepresentative", null);
    }
  };

  const handleRepresentanteChange = (value: string) => {
    if (value !== "yes" && value !== "no") return;
    updateFormData("requiresOwnerRepresentative", value === "yes");
  };

  return (
    <div
      className={`space-y-8 transition-all duration-500 ${isAnimating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-3 pb-4">
          <Settings className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">
            Configuración Final
          </h3>
        </div>

        <div className="space-y-4">
          {/* Convenio de Justicia Alternativa */}
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900">
                Convenio de Justicia Alternativa
              </p>
              <p className="text-xs text-gray-600">
                ¿Quieres añadir el Contrato de Arrendamiento y Convenio de
                Justicia Alternativa?
              </p>
            </div>
            <RadioGroup
              value={convenioValue}
              onValueChange={handleConvenioChange}
              className="mt-4 space-y-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="convenio-yes" />
                <Label htmlFor="convenio-yes" className="text-sm text-gray-700">
                  Sí, proteger mi arrendamiento.
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="convenio-no" />
                <Label htmlFor="convenio-no" className="text-sm text-gray-700">
                  No por ahora.
                </Label>
              </div>
            </RadioGroup>

            {/* Datos del Propietario (solo si hay convenio) */}
            <div
              className={`overflow-hidden transition-all duration-500 ease-out ${
                formData.alternativeJustice
                  ? "max-h-[600px] opacity-100 mt-6"
                  : "max-h-0 opacity-0 mt-0"
              }`}
            >
              <div className="space-y-4 pt-6 border-t border-gray-200">
                {/* Tipo de Persona del Propietario */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Tipo de persona
                  </Label>
                  <RadioGroup
                    value={formData.landlordType}
                    onValueChange={(value) => updateFormData("landlordType", value as "fisica" | "moral")}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  >
                    <label
                      htmlFor="landlord-fisica"
                      className={`relative flex cursor-pointer rounded-md border p-3 transition-all hover:bg-gray-50 ${
                        formData.landlordType === "fisica"
                          ? "border-blue-500 bg-blue-50/50"
                          : "border-gray-300"
                      }`}
                    >
                      <RadioGroupItem
                        value="fisica"
                        id="landlord-fisica"
                        className="mt-0.5"
                      />
                      <div className="ml-2.5">
                        <div className="text-sm font-medium text-gray-900">
                          Persona Física
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Individuos con RFC e identificación
                        </div>
                      </div>
                    </label>
                    <label
                      htmlFor="landlord-moral"
                      className={`relative flex cursor-pointer rounded-md border p-3 transition-all hover:bg-gray-50 ${
                        formData.landlordType === "moral"
                          ? "border-blue-500 bg-blue-50/50"
                          : "border-gray-300"
                      }`}
                    >
                      <RadioGroupItem
                        value="moral"
                        id="landlord-moral"
                        className="mt-0.5"
                      />
                      <div className="ml-2.5">
                        <div className="text-sm font-medium text-gray-900">
                          Persona Moral
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Empresas o entidades legales
                        </div>
                      </div>
                    </label>
                  </RadioGroup>
                </div>

                {/* Correo del Propietario */}
                <div className="space-y-2">
                  <Label htmlFor="landlordEmail" className="text-sm font-medium text-gray-700 mb-2 block">
                    Correo del propietario (opcional)
                  </Label>
                  <Input
                    id="landlordEmail"
                    type="email"
                    value={formData.landlordEmail}
                    onChange={(e) => updateFormData("landlordEmail", sanitizeEmail(e.target.value))}
                    onBlur={() => validateField("landlordEmail")}
                    placeholder="correo@ejemplo.com"
                    className="lowercase"
                    aria-invalid={!!errors.landlordEmail}
                    maxLength={100}
                  />
                  {errors.landlordEmail && (
                    <p className="text-xs text-red-600 mt-1">{errors.landlordEmail}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Al agregar el correo del propietario, le dará acceso al expediente
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Representante del Propietario */}
          <div
            className={`overflow-hidden transition-all duration-500 ease-out ${
              showRepresentanteStep
                ? "max-h-[300px] opacity-100 scale-100"
                : "max-h-0 opacity-0 scale-95"
            }`}
          >
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">
                  Representante del Propietario
                </p>
                <p className="text-xs text-gray-600">
                  ¿Requiere representante del propietario?
                </p>
              </div>
              <RadioGroup
                value={representanteValue}
                onValueChange={handleRepresentanteChange}
                className="mt-4 space-y-3"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="representante-yes" />
                  <Label
                    htmlFor="representante-yes"
                    className="text-sm text-gray-700"
                  >
                    Sí
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="representante-no" />
                  <Label
                    htmlFor="representante-no"
                    className="text-sm text-gray-700"
                  >
                    No
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
